"use client";

import type { AtlasFile } from "@-/prisma";
import { uploadManagedFile } from "@/utils/upload-file";
import type { ManagedUploadPolicy } from "@/utils/upload-policy";
import { useRouter } from "next/navigation";
import type { UploadHandle } from "@/components/ui/Upload";
import { UploadTrigger } from "@/components/ui/Upload";
import type { ReactNode } from "react";
import { forwardRef, useState } from "react";
import { toast } from "sonner";
import { getPreparedUploadAccept, prepareAcceptedUploadFile } from "@/components/ui/Upload/prepare-upload";
type UploadFileTriggerProps = {
  agencyMembershipId?: string;
  organizationMembershipId?: string;
  placementId?: string;
  schoolId?: string;
  projectId?: string;
  accept?: string;
  uploadPolicy?: ManagedUploadPolicy;
  purpose?: "folder";
  onUpload?: (atlasFile: AtlasFile) => Promise<void>;
  children?: (props: {
    isUploading: boolean;
    open: () => void;
  }) => ReactNode;
};
export const UploadFileTrigger = forwardRef<UploadHandle, UploadFileTriggerProps>(({
  agencyMembershipId,
  organizationMembershipId,
  placementId,
  schoolId,
  projectId,
  accept = "*",
  uploadPolicy,
  purpose,
  onUpload,
  children
}, ref) => {
  const router = useRouter();
  const [isUploading, setIsUploading] = useState(false);
  const handleFile = async (file: File) => {
    const toastId = toast.loading("Uploading file...");
    setIsUploading(true);
    try {
      const atlasFile = await uploadManagedFile({
        agencyMembershipId,
        file: await prepareAcceptedUploadFile(file, accept),
        organizationMembershipId,
        placementId,
        schoolId,
        projectId,
        uploadPolicy,
        purpose
      });
      if (onUpload) {
        await onUpload(atlasFile);
      } else {
        toast.success("File uploaded");
      }
      toast.dismiss(toastId);
      // A stored file is not complete until its owning record has been updated.
      router.refresh();
    } catch (error) {
      toast.dismiss(toastId);
      toast.error(error instanceof Error ? error.message : "Failed to upload file");
    } finally {
      setIsUploading(false);
    }
  };
  return <UploadTrigger handleRef={ref} accept={getPreparedUploadAccept(accept)} disabled={isUploading} onFiles={([file_0]) => {
    if (file_0) void handleFile(file_0);
  }}>
      {open => children?.({
      isUploading,
      open
    })}
    </UploadTrigger>;
});
UploadFileTrigger.displayName = "UploadFileTrigger";